import axios from "axios";
import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import toast from "react-hot-toast";
import { Loader } from "../../../utils/Loaders";
import { Blog } from "../Blogs/SeeBlogs";
import { apiCache, createCacheKey } from "../../../utils/apiCache";

interface Bookmark {
  _id: string;
  title: string;
  imageUrl: string;
  summary: string;
  url: string;
}

interface User {
  _id: string;
  username: string;
  email: string;
  posts: any[];
  likesPost: Blog[];
  bookmarks: Bookmark[];
  createdAt: string;
  updatedAt: string;
  profileImageUrl?: string;
}

const Profile = () => {
  const username = localStorage.getItem("name");
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");
  const [bookmarkedArticles, setBookmarkedArticles] = useState<Set<string>>(
    new Set()
  );
  const [likeBlog, setLikeBlog] = useState<Blog[] | null>(null);
  const [isBookmarkOpen, setIsBookmarkOpen] = useState<Boolean>(false);
  const [isLikesOpen, setIsLikesOpen] = useState<boolean>(false);
  const [profileImage, setProfileImage] = useState<string | null>(null);

  const fetchUser = useCallback(async () => {
    if (!username) {
      setError("No username found. Please log in.");
      setLoading(false);
      return;
    }

    const cacheKey = createCacheKey('getUser', { username });
    const cachedData = apiCache.get(cacheKey);
    
    if (cachedData) {
      setUser(cachedData.findUser);
      setLikeBlog(cachedData.LikePost);
      const bookmarkIds = cachedData.findUser.bookmarks.map((b: any) => b._id);
      const bookmarkSet = new Set<string>(bookmarkIds);
      setBookmarkedArticles(bookmarkSet);
      saveBookmarksToLocalStorage(bookmarkSet);
      setProfileImage(cachedData.findUser.profileImageUrl || null);
      setLoading(false);
      return;
    }

    const source = axios.CancelToken.source();
    const timeout = setTimeout(() => {
      source.cancel("Request timed out. Please try again later.");
    }, 10000);

    try {
      const url = process.env.REACT_APP_BACKENDURL;
      const response = await axios.get(`${url}getUser?username=${username}`, {
        cancelToken: source.token,
        timeout: 8000,
      });
      
      if (response.data.success) {
        const fetchedUser: User = response.data.findUser;
        setUser(fetchedUser);
        setLikeBlog(response.data.LikePost);

        const bookmarkIds = fetchedUser.bookmarks.map((b) => b._id);
        const bookmarkSet = new Set<string>(bookmarkIds);
        setBookmarkedArticles(bookmarkSet);
        saveBookmarksToLocalStorage(bookmarkSet);
        setProfileImage(fetchedUser.profileImageUrl || null);
        
        apiCache.set(cacheKey, response.data);
      } else {
        setError(response.data.message || "Failed to fetch user data.");
      }
    } catch (err: any) {
      if (axios.isCancel(err)) {
        setError("Request timed out. Please try again later.");
      } else {
        setError(
          err.response?.data?.message ||
            "An error occurred while fetching user data."
        );
        console.error("Error fetching user:", err);
      }
    } finally {
      clearTimeout(timeout);
      setLoading(false);
    }
  }, [username]);

  const saveBookmarksToLocalStorage = (bookmarks: Set<string>) => {
    localStorage.setItem(
      "bookmarkedArticles",
      JSON.stringify(Array.from(bookmarks))
    );
  };

  const handleRemoveBookmark = async (bookmark: Bookmark) => {
    if (!username) {
      toast.error("You need to be logged in to remove bookmarks.");
      return;
    }

    try {
      const url = process.env.REACT_APP_BACKENDURL;
      const response = await axios.delete(`${url}removeBookmark`, {
        data: {
          username,
          bookmarkId: bookmark._id,
        },
        timeout: 5000,
      });

      if (response.data.success) {
        const updatedBookmarks = new Set(bookmarkedArticles);
        updatedBookmarks.delete(bookmark._id);
        setBookmarkedArticles(updatedBookmarks);
        saveBookmarksToLocalStorage(updatedBookmarks);
        
        if (user) {
          setUser({
            ...user,
            bookmarks: user.bookmarks.filter((b) => b._id !== bookmark._id),
          });
        }
        
        toast.success("Bookmark removed successfully");
        
        const cacheKey = createCacheKey('getUser', { username });
        apiCache.delete(cacheKey);
      }
    } catch (error: any) {
      toast.error("Failed to remove bookmark");
      console.error("Error removing bookmark:", error);
    }
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = async () => {
        setProfileImage(reader.result as string);
        const formData = new FormData();
        formData.append('profilePic', file);
        const userId=localStorage.getItem("name");
        try {
          const response = await axios.put(
            `${process.env.REACT_APP_BACKENDURL}/uploadUserProfilePic/${userId}`,
            {file:formData},
            {
              headers: {
                'Content-Type': 'multipart/form-data',
              },
            }
          );
          console.log('Image uploaded successfully:', response.data);
        } catch (error) {
          console.error('Error uploading image:', error);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white flex flex-col items-center p-6">
      <h1 className="text-4xl font-bold mb-8">Profile Page</h1>

      {loading ? (
        <Loader />
      ) : error ? (
        <div className="bg-red-600 p-4 rounded-lg shadow-md">
          <p className="text-white">{error}</p>
        </div>
      ) : user ? (
        <motion.div
          className="bg-gray-800 p-8 rounded-lg shadow-lg w-full max-w-4xl"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
        >
          <div className="flex flex-col items-center mb-6">
            <div className="relative w-24 h-24 bg-gray-700 rounded-full mb-4 flex items-center justify-center overflow-hidden">
              {profileImage ? (
                <img
                  src={profileImage}
                  alt="Profile"
                  className="w-full h-full rounded-full object-cover"
                />
              ) : (
                <label className="absolute inset-0 flex items-center justify-center cursor-pointer">
                  <span className="text-4xl text-white">+</span>
                  <input
                    type="file"
                    accept="image/png, image/jpeg"
                    onChange={handleImageUpload}
                    className="absolute inset-0 opacity-0 cursor-pointer"
                  />
                </label>
              )}
            </div>
            <h2 className="text-2xl font-semibold">
              Welcome, {user.username}!
            </h2>
            <p className="text-gray-400">
              Member since {formatDate(user.createdAt)}
            </p>
          </div>

          <div className="bg-gray-700 p-6 rounded-md mb-6">
            <h3 className="text-lg font-medium mb-2">User Information</h3>
            <p className="text-gray-300">
              <strong>Username:</strong> {user.username}
            </p>
            <p className="text-gray-300">
              <strong>Email:</strong> {user.email}
            </p>
          </div>

          <div className="flex justify-between mb-6">
            <button
              className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md transition-colors duration-200"
              onClick={() => toast("Edit Profile functionality coming soon!")}
            >
              Edit Profile
            </button>
          </div>

          <div className="pt-5">
            <div
              className="bg-gray-800 p-4 rounded-md flex justify-between items-center cursor-pointer"
              onClick={() => setIsBookmarkOpen(!isBookmarkOpen)}
            >
              <h3 className="text-2xl font-semibold">Key Highlights :- </h3>
              <span>{isBookmarkOpen ? "▲" : "▼"}</span>
            </div>
            {isBookmarkOpen && (
              <div className="mt-4">
                {user.bookmarks.length === 0 ? (
                  <p className="text-gray-400">You have no bookmarks yet.</p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {user.bookmarks.map((bookmark) => (
                      <motion.div
                        key={bookmark._id}
                        className="bg-gray-800 p-4 rounded-lg shadow-md hover:shadow-xl transition-shadow duration-300 flex flex-col justify-between"
                        whileHover={{ scale: 1.05 }}
                        initial={{ opacity: 0, y: 50 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                      >
                        <div>
                          <img
                            src={bookmark.imageUrl}
                            alt={bookmark.title}
                            className="w-full h-48 object-cover rounded-md mb-2"
                          />
                          <h4 className="text-lg font-semibold">
                            {bookmark.title}
                          </h4>
                          <p className="text-gray-400">{bookmark.summary}</p>
                        </div>
                        <div className="flex justify-between mt-2">
                          <a
                            href={bookmark.url}
                            className="text-blue-400 hover:underline"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Read More
                          </a>
                          <button
                            className="text-red-500 hover:underline"
                            onClick={() => handleRemoveBookmark(bookmark)}
                          >
                            Remove
                          </button>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="pt-5">
            <div
              className="bg-gray-800 p-4 rounded-md flex justify-between items-center cursor-pointer"
              onClick={() => setIsLikesOpen(!isLikesOpen)}
            >
              <h3 className="text-2xl font-semibold">Liked Blogs :- </h3>
              <span>{isLikesOpen ? "▲" : "▼"}</span>
            </div>
            {isLikesOpen && (
              <div className="mt-4">
                {likeBlog?.length === 0 ? (
                  <p className="text-gray-400">You have no liked blogs yet.</p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {likeBlog?.map((blog) => (
                      <motion.div
                        key={blog._id}
                        className="bg-gray-800 p-4 rounded-lg shadow-md hover:shadow-xl transition-shadow duration-300"
                        whileHover={{ scale: 1.05 }}
                        initial={{ opacity: 0, y: 50 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                      >
                        <h4 className="text-lg font-semibold">{blog.title}</h4>
                        <p className="text-gray-400">{blog.description}</p>
                      </motion.div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </motion.div>
      ) : (
        <p className="text-gray-400">No user data available.</p>
      )}
    </div>
  );
};

export default Profile;
